import React, { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import LoadingSpinner from './LoadingSpinner';
import { useAppStore } from '../stores/appStore';
import { useIapStore } from '../stores/iapStore';
import { withTimeout, getCachedAuthUser } from '../../services/network';

const AUTH_TIMEOUT_MS = 4000;

// The owned-receipt marker is scoped to the signed-in Cognito sub, so it is
// re-read whenever this guard establishes a session (sign-in, relaunch, a
// different account on a shared device). Safe to call on every transition.
function refreshIapMarker() {
  try {
    useIapStore.getState().refreshFromStorage();
  } catch {
    /* never block the guard on the IAP store */
  }
}

export default function PrivateRoute() {
  const [authState, setAuthState] = useState('loading');
  const setUser = useAppStore((s) => s.setUser);

  useEffect(() => {
    let cancelled = false;

    withTimeout(Auth.currentAuthenticatedUser(), AUTH_TIMEOUT_MS, 'Auth check timed out')
      .then((u) => {
        if (cancelled) return;
        const payload = u?.signInUserSession?.idToken?.payload || {};
        setUser({
          sub: payload.sub || u?.username,
          username: u?.username,
          email: payload.email || u?.attributes?.email,
        });
        refreshIapMarker();
        setAuthState('authenticated');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.isTimeoutError) {
          const cached = getCachedAuthUser();
          if (cached) {
            setUser({ sub: cached.sub, username: cached.username, email: cached.email });
            refreshIapMarker();
            setAuthState('authenticated');
            return;
          }
        }
        setUser(null);
        setAuthState('unauthenticated');
      });

    return () => {
      cancelled = true;
    };
  }, [setUser]);

  if (authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    // The welcome screen is the signed-out landing since 1.5.0 (Get started /
    // Sign in); a session that expires mid-use still goes to /login from the
    // API client's 401 handler.
    return <Navigate to="/welcome" replace />;
  }

  return <Outlet />;
}
